// # Admin Notifications Page Component
import React, { useEffect, useState } from 'react';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { BsCheckCircle, BsPlayCircle, BsArrowClockwise, BsStopCircle, BsPencil, BsAlarm, BsTrash, BsChatDots } from 'react-icons/bs';
import { FaChalkboardTeacher, FaWpforms } from 'react-icons/fa';
import sessionManager from '../../../auth/session';
import notificationManager from '../../../auth/sessions/notification_session';
import './notif_page.css';

// # Get icon based on notification action
const getNotificationIcon = (action) => {
  switch (action) {
    case 'activate':
      return <BsPlayCircle className="notif_page_item_icon activate" />;
    case 'extend':
      return <BsArrowClockwise className="notif_page_item_icon extend" />;
    case 'reduce':
      return <BsArrowClockwise className="notif_page_item_icon reduce" />;
    case 'deactivate':
      return <BsStopCircle className="notif_page_item_icon deactivate" />;
    case 'ended':
      return <BsStopCircle className="notif_page_item_icon ended" />;
    case 'assign_mentor':
      return <FaChalkboardTeacher className="notif_page_item_icon mentor" />;
    case 'edit':
      return <BsPencil className="notif_page_item_icon edit" />;
    case 'new_subscription_request':
      return <FaWpforms className="notif_page_item_icon subscription_form" />;
    case 'new_feedback':
      return <BsChatDots className="notif_page_item_icon feedback" />;
    case 'new_review':
      return <BsChatDots className="notif_page_item_icon review" />;
    default:
      return null;
  }
};

const AdminNotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  
  useEffect(() => {
    const initializeAdminNotifications = async () => {
      const session = sessionManager.getSession();
      if (!session?.uid) return;

      // # Initialize notifications first
      await notificationManager.initializeNotifications(session.uid);
      
      // # Get notifications from session cache
      const notificationState = notificationManager.getNotifications();
      if (notificationState?.adminNotifications) {
        const allNotifications = [];
        
        // # Add subscription notifications
        if (notificationState.adminNotifications.subscriptions) {
          Object.values(notificationState.adminNotifications.subscriptions).forEach(notif => {
            allNotifications.push({
              ...notif,
              notificationType: 'subscription'
            });
          });
        }
        
        // # Add subscription form notifications
        if (notificationState.adminNotifications.subscription_forms) {
          Object.values(notificationState.adminNotifications.subscription_forms).forEach(notif => {
            allNotifications.push({
              ...notif,
              notificationType: 'subscription_form'
            });
          });
        }

        // # Add feedback notifications
        if (notificationState.adminNotifications.feedbacks) {
          Object.values(notificationState.adminNotifications.feedbacks).forEach(notif => {
            allNotifications.push({
              ...notif,
              notificationType: 'feedback'
            });
          });
        }

        // # Add review notifications
        if (notificationState.adminNotifications.reviews) {
          Object.values(notificationState.adminNotifications.reviews).forEach(notif => {
            allNotifications.push({
              ...notif,
              notificationType: 'review'
            });
          });
        }

        // # Sort by creation date (show all notifications)
        const sortedNotifications = allNotifications
          .sort((a, b) => new Date(b.metadata.createdAt) - new Date(a.metadata.createdAt));
        
        setNotifications(sortedNotifications);
      }
    };

    // Initialize on component mount
    initializeAdminNotifications();

    // Setup cleanup
    return () => {
      const session = sessionManager.getSession();
      if (session?.uid) {
        notificationManager.removeNotificationListeners(session.uid);
      }
    };
  }, []);

  const handleNotificationClick = async (notificationId, notificationType) => {
    try {
      const session = sessionManager.getSession();
      if (!session?.uid) return;
      
      // # Mark notification as seen
      await notificationManager.markAdminNotificationAsSeen(notificationId, session.uid, notificationType);
      // # Update local state
      setNotifications(prev => prev.map(notif => 
        notif.id === notificationId ? { ...notif, seen: true } : notif
      ));
    } catch (error) {
      console.error('Error marking notification as seen:', error);
    }
  };

  const handleMarkAllAsSeen = async () => {
    try {
      const session = sessionManager.getSession();
      if (!session?.uid) return;
      
      await notificationManager.markAllAdminNotificationsAsSeen(session.uid);
      // # Update local state
      setNotifications(prev => prev.map(notif => ({ ...notif, seen: true })));
    } catch (error) {
      console.error('Error marking all notifications as seen:', error);
    }
  };

  const handleDeleteNotification = async (notificationId, notificationType, event) => {
    event.stopPropagation();
    try {
      const session = sessionManager.getSession();
      if (!session?.uid) return;
      
      // # Delete notification from session
      await notificationManager.deleteAdminNotification(notificationId, session.uid, notificationType);
      // # Update local state
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleClearAll = async () => {
    try {
      const session = sessionManager.getSession();
      if (!session?.uid) return;
      
      // # Clear all notifications from session
      await notificationManager.clearAllAdminNotifications(session.uid);
      // # Update local state
      setNotifications([]);
    } catch (error) {
      console.error('Error clearing all notifications:', error);
    }
  };

  const toggleExpand = (notificationId, event) => {
    event.stopPropagation();
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  // # Helper function to format subscription type
  const getFormattedSubscriptionType = (type) => {
    if (!type) return 'Basic Plan';
    
    switch(type.toLowerCase()) {
      case 'step1_library':
        return 'USMLE Step 1 Library';
      case 'step2_library':
        return 'USMLE Step 2 Library';
      case 'step1_mentorship':
        return 'USMLE Step 1 Mentorship';
      case 'step2_mentorship':
        return 'USMLE Step 2 Mentorship';
      case 'oet':
        return 'OET Preparation';
      case 'us_visa_counselling':
        return 'US VISA Counselling';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
    }
  };

  return (
    <>
      <DashboardHeader />
      <div className="notif_page_wrapper">
        <div className="notif_page_container">
          <div className="notif_page_header">
            <div className="notif_page_title_container">
              <h1 className="notif_page_title">Admin Notifications</h1>
              <div className="notif_page_title_decoration"></div>
            </div>
            <p className="notif_page_subtitle">Manage and view all system notifications</p>
            {notifications.length > 0 && (
              <div className="notif_page_actions">
                <button 
                  className="notif_page_mark_all" 
                  onClick={handleMarkAllAsSeen}
                >
                  <BsCheckCircle /> Mark all as read
                </button>
                <button 
                  className="notif_page_clear_all" 
                  onClick={handleClearAll}
                >
                  <BsTrash /> Clear all notifications
                </button>
              </div>
            )}
          </div>
          
          <div className="notif_page_list">
            {notifications.length > 0 ? (
              notifications.map((notification) => (
                <div 
                  key={notification.id}
                  className={`notif_page_item ${notification.seen ? 'seen' : ''} ${expandedIds.has(notification.id) ? 'expanded' : ''}`}
                  onClick={(e) => toggleExpand(notification.id, e)}
                  data-action={notification.metadata.action}
                >
                  {getNotificationIcon(notification.metadata.action)}
                  <div className="notif_page_item_content">
                    <div className="notif_page_item_header">
                      <div>
                        <span className="notif_page_item_title">
                          {notification.metadata.action === 'new_subscription_request' 
                            ? 'New subscription request'
                            : notification.metadata.action === 'new_feedback'
                            ? 'New feedback received'
                            : notification.metadata.action === 'new_review'
                            ? 'New review received'
                            : notification.metadata.action === 'activate' ? 'Subscription activated'
                            : notification.metadata.action === 'extend' ? 'Subscription extended'
                            : notification.metadata.action === 'reduce' ? 'Subscription duration reduced'
                            : notification.metadata.action === 'deactivate' ? 'Subscription deactivated'
                            : notification.metadata.action === 'ended' ? 'Subscription ended'
                            : notification.metadata.action === 'assign_mentor' ? 'Mentor assigned'
                            : notification.metadata.action === 'edit' ? 'Subscription updated'
                            : 'Subscription status changed'}
                        </span>
                        <span className="notif_page_item_time">
                          {new Date(notification.metadata.createdAt).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div className="notif_page_item_actions">
                        <BsCheckCircle 
                          className={`notif_page_item_check ${notification.seen ? 'seen' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNotificationClick(notification.id, notification.notificationType);
                          }}
                          title="Mark as read"
                        />
                        <BsTrash 
                          className="notif_page_item_delete"
                          onClick={(e) => handleDeleteNotification(notification.id, notification.notificationType, e)}
                          title="Delete notification"
                        />
                      </div>
                    </div>
                    {notification.metadata.details && (
                      <p className="notif_page_item_details">
                        {notification.metadata.details}
                      </p>
                    )}
                    {expandedIds.has(notification.id) && (
                      <div className="notif_page_item_expanded">
                        {notification.metadata.action === 'new_subscription_request' ? (
                          <>
                            <p>Customer: {notification.metadata.formData.name}</p>
                            <p>Username: {notification.metadata.formData.username}</p>
                            <p>Email: {notification.metadata.formData.email}</p>
                            <p>Phone: {notification.metadata.formData.phoneCode} {notification.metadata.formData.phoneNumber}</p>
                            <p>Nationality: {notification.metadata.formData.nationality}</p>
                            <p>Plan: {getFormattedSubscriptionType(notification.metadata.formData.product)}</p>
                            <p>Duration: {notification.metadata.formData.duration}</p>
                            <p>University: {notification.metadata.formData.university}</p>
                            <p>Phase: {notification.metadata.formData.phase}</p>
                            <p>GPA: {notification.metadata.formData.gpa}</p>
                            <p>Pricing: {notification.metadata.formData.pricing === 'egyptian' ? 'Egyptian Pricing' : 'International Pricing'}</p>
                            <p>Request ID: {notification.metadata.formData.requestId}</p>
                          </>
                        ) : notification.metadata.action === 'new_feedback' ? (
                          <>
                            <p>From: {notification.metadata.feedbackData.name}</p>
                            <p>Email: {notification.metadata.feedbackData.email}</p>
                            <p>Content: {notification.metadata.feedbackData.content}</p>
                            <p>Feedback ID: {notification.metadata.feedbackData.feedbackId}</p>
                          </>
                        ) : notification.metadata.action === 'new_review' ? (
                          <>
                            <p>From: {notification.metadata.reviewData.userName}</p>
                            <p>Email: {notification.metadata.reviewData.email}</p>
                            <p>Product: {getFormattedSubscriptionType(notification.metadata.reviewData.product)}</p>
                            <p>Content: {notification.metadata.reviewData.content}</p>
                          </>
                        ) : (
                          <>
                            <p>Customer Plan: {getFormattedSubscriptionType(notification.type)}</p>
                            <p>Active Until: {notification.endDate ? 
                              new Date(notification.endDate).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              }) : 
                              'Ongoing subscription'}
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="notif_page_empty">
                No notifications to display
              </div>
            )}
          </div>
        </div>
      </div>
      <DashboardFooter />
    </>
  );
};

export default AdminNotificationsPage; 